import React, { useEffect, useRef, useState } from 'react';
import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { calculatePickAccuracy } from '../services/scoringService';
import './LeaderboardTab.css';

export default function LeaderboardTab({ leagueId, uid }) {
  const [rows, setRows]       = useState(null);
  const namesCache             = useRef({});   // userId → username

  useEffect(() => {
    let mounted = true;
    const q = query(collection(db, 'leagueMembers'), where('leagueId', '==', leagueId));
    const unsub = onSnapshot(q, async (snap) => {
      const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Fetch missing display names
      const missing = raw.filter((m) => !namesCache.current[m.userId]);
      if (missing.length) {
        try {
          await Promise.all(
            missing.map(async (m) => {
              const s = await getDoc(doc(db, 'users', m.userId));
              namesCache.current[m.userId] = s.exists() ? s.data().username : 'Unknown';
            })
          );
        } catch (_) {
          // name fetch failure is non-fatal; show '…' fallback
        }
      }

      if (!mounted) return;

      // Sort: totalPoints desc, joinedAt asc for ties
      const sorted = [...raw].sort((a, b) => {
        const diff = (b.totalPoints || 0) - (a.totalPoints || 0);
        if (diff !== 0) return diff;
        return (a.joinedAt?.seconds ?? 0) - (b.joinedAt?.seconds ?? 0);
      });

      // Shared rank
      let rank = 1;
      const ranked = sorted.map((m, i) => {
        if (i > 0 && (m.totalPoints || 0) < (sorted[i - 1].totalPoints || 0)) rank = i + 1;
        return { ...m, rank, username: namesCache.current[m.userId] ?? '…' };
      });

      // Leader's points for elimination check
      const leaderPoints = ranked.length ? (ranked[0].totalPoints || 0) : 0;

      // Attach elimination flag (use stored mpp if available)
      const final = ranked.map((m) => ({
        ...m,
        isEliminated: m.mpp != null && m.mpp < leaderPoints && leaderPoints > 0,
      }));

      setRows(final);
    }, (err) => console.error('leaderboard subscription error:', err));
    return () => { mounted = false; unsub(); };
  }, [leagueId]);

  if (!rows) {
    return <div className="lbt-loading">Loading leaderboard…</div>;
  }

  return (
    <div className="lbt">
      {/* Header row */}
      <div className="lbt-header">
        <span className="lbt-col lbt-col--rank">#</span>
        <span className="lbt-col lbt-col--name">Player</span>
        <span className="lbt-col lbt-col--pts">Pts</span>
        <span className="lbt-col lbt-col--mpp">MPP</span>
        <span className="lbt-col lbt-col--acc">Acc%</span>
        <span className="lbt-col lbt-col--status">Status</span>
      </div>

      {rows.map((m) => {
        const isMe   = m.userId === uid;
        const isGold = m.rank === 1;
        const isOut  = m.isEliminated;
        const acc    = calculatePickAccuracy(m.correctPicks || 0, m.totalPicks || 0);

        return (
          <div
            key={m.id}
            className={[
              'lbt-row',
              isGold ? 'lbt-row--gold' : '',
              isOut  ? 'lbt-row--out'  : '',
              isMe   ? 'lbt-row--me'   : '',
            ].filter(Boolean).join(' ')}
          >
            <span className="lbt-col lbt-col--rank">
              {isGold ? '🥇' : `#${m.rank}`}
            </span>
            <span className="lbt-col lbt-col--name">{m.username}</span>
            <span className="lbt-col lbt-col--pts">{m.totalPoints ?? 0}</span>
            <span className="lbt-col lbt-col--mpp">
              {m.mpp != null ? m.mpp : '—'}
            </span>
            <span className="lbt-col lbt-col--acc">
              {m.totalPicks ? `${acc.toFixed(1)}%` : '—'}
            </span>
            <span className="lbt-col lbt-col--status">
              {isOut
                ? <span className="lbt-badge lbt-badge--out">OUT</span>
                : <span className="lbt-badge lbt-badge--in">Active</span>
              }
            </span>
          </div>
        );
      })}

      {rows.length === 0 && (
        <div className="lbt-empty">No members yet.</div>
      )}
    </div>
  );
}
